/**
 * @email       : saraman@ibl.mx
 * @author      : Arman Ali
 * @title       : Full Stack Developer
 * @github      : https://github.com/Arman-Arzoo
 * @hook        : ApprovalApp
 * @description : Custom hook for managing the state and logic related to the Approval system.
 *                This includes local states for selection, searching, modals, and permissions,
 *                as well as dispatching the Redux action to fetch approval data.
 *
 * @dependencies:
 * - Redux (for fetching and accessing approval data and permissions)
 * - js-cookie (for getting the selected language)
 * - i18next (for translations)
 *
 * @returns {Object} An object containing state and functions for approval management.
 */

import React, {useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteApproval, GetApprovalById } from '../../reduxToolkit/Approval/ApprovalApi';
import { useNavigate } from 'react-router-dom';
import routes from '../../Routes/routes';
import { SimpleSearch } from '../../reduxToolkit/Search/SearchApi';
import { TABLES } from '../../Apis/Tables';
import TableFunction from "../../components/table/TableFunction";
import DropDownMenuProfile from "../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../components/DropDown/DropDownMenuItem";
import {SEARCH_MODEL} from "../../components/table/Model/TableModel";
import {DeleteIconsOptions, UpdateIconsOptions} from "../../components/buttons/IconButtons";
import RenderExpandedRow from "./RenderExpandedRow";
import {colors} from "../../enums/colors";
import CustomChip from "../../components/CustomChip";
import {permissionObj} from "../../Helpers/permission";
import {toast} from "react-toastify";
import {getModelKeyById} from "../../Apis/Models";
import {VIEW_TYPE} from "../../enums/ViewType";
import {EMPTY_VALUES, errorMsg} from "../../Apis/functions";

const ApprovalApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { getApprovalById } = useSelector((state) => state?.ApprovalSlice);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [modified, setModified] = useState(false);
    const [search, setSearch] = useState(false); // Search flag
    const [bodyForExport, setBodyForExport] = useState(''); // Data to export
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const searchId = 'txt_af_search_approval';
    const option = TABLES.APPROVAL;
    const [results, setResults] = useState([]);
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));

    const deleteApprovalFn = (ids) => {
        dispatch(
            DeleteApproval({
                table: TABLES.APPROVAL,
                ids,
            }),
        ).then(({ payload: { data: { success, code } } }) => {
            if(success){
                setModified(true);
                setSelectedList([]);
                toast.success(t('has_been_deleted_successfully'));
            }else errorMsg(code)
        });
    };

    const renderOptionsMenu = (item, index) => {
        return <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-${item.id}`} // Unique menu ID for each contractor
        >
            {(DEV || permission.includes(permissionObj?.WEB_APPROVAL_FLOW_UPDATE)) &&
                <DropDownMenuItem
                    id={"btn_aa_update_"+ item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    click={() => {
                        // Navigate to the APPROVAL  update route with item ID
                        navigate(routes.approval.update, {
                            state: { id: item.id }
                        });
                    }} // Navigate to the update page
                    icon={<UpdateIconsOptions/>}
                />
            }

            {(DEV || permission.includes(permissionObj?.WEB_APPROVAL_FLOW_DELETE)) &&
                <DropDownMenuItem
                    id={"btn_aa_delete_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('delete')}
                    click={() => {
                        setSelectedList([item?.id]); // Set selected item ID for deletion
                        setDeleteModal(true);
                    }}
                    icon={<DeleteIconsOptions/>}
                    fontColor={'font-color-error-600'}
                    iconColor={"icon-color-error-code-600"}
                />
            }
        </DropDownMenuProfile>
    };

    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            dispatch(GetApprovalById(row?.id));
            setExpandedRowId(row?.id);
        }
    };

    const columns = [
        { field: 'approvalFlowName', label: 'Name' },
        { field: 'department.name', label: 'Department' },
        { field: 'module', label: 'module',
            render: (value) => <CustomChip
                background={colors.secondary100}
                fontColor={colors.secondary700}
                borderColor={colors.secondary150}
                legend={t(getModelKeyById(value?.id)) || EMPTY_VALUES.DEFAULT} variant={'large'}
            />,
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, key:searchElement});
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);  // Update the results state if data is successful
                }
            }
        );
    };

    // Use the TableFunction component to handle table logic (checkbox selection, pagination, etc.)
    const { t, handleCheckboxChange, handleDeleteAll, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose} = TableFunction({
        selectedList,
        setSelectedList, // Callback to update the selected rows
        data: results, // Table data
        labelRowsPerPage: 'approval_per_page', // Label for rows per page
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        setIsAllChecked,
        view:VIEW_TYPE.GRID.name,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    const renderExpandedRow = () => {
        const levels = [...(getApprovalById?.levels || [])]
            .sort((a, b) => a.levelNumber - b.levelNumber);
        return <RenderExpandedRow levels={levels} t={t}/>;
    };

    return {
        t,
        deleteModal,
        setDeleteModal,
        setIsAllChecked,
        DEV,
        permission,
        selectedList,
        columns,
        results,
        setResults,
        searchElement,
        setSearchElement,
        search, setSearch,
        option,
        searchId,
        setSelectedList,
        page,
        rowsPerPage,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        contractorId,
        supplierId,
        pagination,
        deleteApprovalFn,
        handleCheckboxChange,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        renderExpandedRow
    };
};

export default ApprovalApp;
